// MemoryContext.jsx
// Two contexts in one file:
//   AuthContext  — current user, JWT token, login/logout helpers
//   MemoryContext — user's central memory record (loaded after login)
//
// Both are provided by a single top-level wrapper so every page and
// component can access auth state and memory without prop drilling.

import { createContext, useContext, useState } from 'react';
import { setAuthToken } from '../api';

// ─── Auth ────────────────────────────────────────────────────────────────────

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // { id, email } once logged in
  const [token, setToken] = useState(null); // JWT returned by the backend

  function login(userData, jwt) {
    // Store the authenticated user and their JWT.
    // Also registers the token with api.js so every subsequent request
    // gets the Authorization header automatically.
    setAuthToken(jwt);
    setUser(userData);
    setToken(jwt);
  }

  function logout() {
    // Clear auth state and remove token from api.js.
    // App.jsx will redirect to /login via the protected route.
    setAuthToken(null);
    setUser(null);
    setToken(null);
  }

  function isAuthenticated() {
    return user !== null && token !== null;
  }

  const authValue = { user, token, login, logout, isAuthenticated };

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

// ─── Memory ──────────────────────────────────────────────────────────────────

export const MemoryContext = createContext(null);

export function MemoryProvider({ children }) {
  // Holds the user's central memory record once it is fetched from the backend.
  // Updated at the end of every agent session via useMemory hook.
  const [memory, setMemory] = useState(null);

  const memoryValue = { memory, setMemory };

  return (
    <MemoryContext.Provider value={memoryValue}>{children}</MemoryContext.Provider>
  );
}

export function useMemoryContext() {
  return useContext(MemoryContext);
}
